import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogEmphasis,
  DialogHeader,
  DialogTitle,
} from 'components/ui';
import FirmDocumentDialogSearchableSelect from 'pages/Documents/FirmDocuments/components/FirmDocumentDialogSearchableSelect';
import FirmDocumentFolderPathLabel from 'pages/Documents/FirmDocuments/components/FirmDocumentFolderPathLabel';
import type { FirmDocumentFolderCreateDialogProps } from './FirmDocumentFolderCreateDialog.props';
import {
  FirmDocumentFolderCreateDialogField,
  FirmDocumentFolderCreateDialogFieldGroup,
  FirmDocumentFolderCreateDialogInput,
  FirmDocumentFolderCreateDialogLabel,
} from './FirmDocumentFolderCreateDialog.styles';
import { FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS } from './FirmDocumentFolderCreateDialog.testIds';

const MIN_FOLDER_NAME_LENGTH = 3;
const FOLDER_NAME_INPUT_ID = 'firm-documents-folder-create-name';
const ROOT_PARENT_FOLDER_OPTION = { id: null, label: 'No Folder' };
const DEFAULT_PARENT_FOLDER_OPTIONS = [ROOT_PARENT_FOLDER_OPTION];

const normalizeFolderName = (folderName: string) => folderName.trim().toLowerCase();
const getFolderCreateTargetKey = (mdDocumentsId: number | null | undefined, parentFolderId: number | null) =>
  `${mdDocumentsId ?? 'missing'}:${parentFolderId ?? 'root'}`;

const getFolderNameHelperText = ({
  duplicateFolderName,
  folderName,
}: {
  duplicateFolderName: boolean;
  folderName: string;
}) => {
  if (!folderName.trim()) {
    return 'Folder name is required.';
  }

  if (folderName.trim().length < MIN_FOLDER_NAME_LENGTH) {
    return `Folder name must be at least ${MIN_FOLDER_NAME_LENGTH} characters.`;
  }

  if (duplicateFolderName) {
    return 'A folder with this name already exists in this location.';
  }

  return undefined;
};

const FirmDocumentFolderCreateDialog = ({
  companyOptions = [],
  initialCompanyId = null,
  initialParentFolderId = null,
  isCreating = false,
  measurementDateLabel = null,
  mdDocumentsId = null,
  onClose,
  onConfirm,
  open,
  parentFolderOptions = DEFAULT_PARENT_FOLDER_OPTIONS,
  parentFolderOptionsByCompanyId = {},
  parentFolderName = null,
  siblingFolderNames = [],
  siblingFolderNamesByTargetKey = {},
  targetCompanyName = null,
}: FirmDocumentFolderCreateDialogProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [folderName, setFolderName] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(initialCompanyId);
  const [selectedParentFolderId, setSelectedParentFolderId] = useState<number | null>(initialParentFolderId);
  const shouldSelectCompany = companyOptions.length > 0;
  const selectedCompanyOption = companyOptions.find(companyOption => companyOption.id === selectedCompanyId) ?? null;
  const activeMdDocumentsId = shouldSelectCompany ? selectedCompanyOption?.mdDocumentsId ?? null : mdDocumentsId;
  const activeParentFolderOptions = useMemo(
    () => (
      shouldSelectCompany
        ? parentFolderOptionsByCompanyId[selectedCompanyId ?? -1] ?? DEFAULT_PARENT_FOLDER_OPTIONS
        : parentFolderOptions
    ),
    [parentFolderOptions, parentFolderOptionsByCompanyId, selectedCompanyId, shouldSelectCompany]
  );
  const selectedParentOption = activeParentFolderOptions.find(option => option.id === selectedParentFolderId) ?? null;
  const activeSiblingFolderNames = siblingFolderNamesByTargetKey[
    getFolderCreateTargetKey(activeMdDocumentsId, selectedParentFolderId)
  ] ?? siblingFolderNames;
  const trimmedFolderName = folderName.trim();
  const normalizedSiblingFolderNames = useMemo(
    () => new Set(activeSiblingFolderNames.map(normalizeFolderName)),
    [activeSiblingFolderNames]
  );
  const duplicateFolderName = Boolean(trimmedFolderName)
    && normalizedSiblingFolderNames.has(normalizeFolderName(trimmedFolderName));
  const validationText = getFolderNameHelperText({ duplicateFolderName, folderName });
  const helperText = hasAttemptedSubmit || folderName ? validationText : undefined;
  const canCreateFolder = Boolean(
    trimmedFolderName
      && !validationText
      && !isCreating
      && activeMdDocumentsId
      && selectedParentOption
      && (!shouldSelectCompany || selectedCompanyOption)
  );
  const activeTargetCompanyName = shouldSelectCompany ? selectedCompanyOption?.label : targetCompanyName;
  const rootTargetLabel = selectedCompanyOption?.label
    ?? activeTargetCompanyName
    ?? parentFolderName
    ?? measurementDateLabel
    ?? 'this measurement date';
  const selectedParentTargetLabel = activeTargetCompanyName
    ? `${activeTargetCompanyName} > ${selectedParentOption?.label}`
    : selectedParentOption?.label;
  const targetLabel = selectedParentOption?.id
    ? <FirmDocumentFolderPathLabel label={selectedParentTargetLabel ?? selectedParentOption.label} variant="wrap" />
    : rootTargetLabel;

  const handleConfirm = () => {
    setHasAttemptedSubmit(true);

    if (canCreateFolder && activeMdDocumentsId) {
      onConfirm({
        folderName: trimmedFolderName,
        mdDocumentsId: activeMdDocumentsId,
        parentFolderId: selectedParentFolderId,
      });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirm();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  // Reset transient form state from the requested folder target when the dialog opens.
  useEffect(() => {
    if (!open) {
      return;
    }

    setFolderName('');
    setHasAttemptedSubmit(false);
    setSelectedCompanyId(initialCompanyId);
    setSelectedParentFolderId(initialParentFolderId);
  }, [initialCompanyId, initialParentFolderId, open]);

  // Keep the parent selector valid when the company target changes in All Documents.
  useEffect(() => {
    if (!open || activeParentFolderOptions.some(option => option.id === selectedParentFolderId)) {
      return;
    }

    setSelectedParentFolderId(null);
  }, [activeParentFolderOptions, open, selectedParentFolderId]);

  // Move focus directly into the folder name field when users open this action menu flow.
  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus());
    }
  }, [open]);

  return (
    <Dialog
      data-testid={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.dialog}
      fullWidth
      maxWidth="xs"
      onClose={isCreating ? undefined : onClose}
      open={open}>
      <DialogHeader>
        <DialogTitle>Add Folder</DialogTitle>
        <DialogCloseButton
          aria-label="Close add folder dialog"
          data-testid={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.closeButton}
          disabled={isCreating}
          onClick={onClose}
          size="small">
          <DialogCloseIcon />
        </DialogCloseButton>
      </DialogHeader>
      <DialogContent>
        <DialogDescription>
          {selectedParentOption?.id ? (
            <span>
              Create a subfolder inside <DialogEmphasis>{targetLabel}</DialogEmphasis>.
            </span>
          ) : (
            <span>
              Create a folder at the top level of <DialogEmphasis>{targetLabel}</DialogEmphasis>.
            </span>
          )}
        </DialogDescription>
        <FirmDocumentFolderCreateDialogFieldGroup>
          {shouldSelectCompany ? (
            <FirmDocumentDialogSearchableSelect
              clearSearchAriaLabel="Clear company search"
              disabled={isCreating || companyOptions.length === 0}
              emptyLabel="No companies found"
              itemLabelPlural="companies"
              itemLabelSingular="company"
              label="Company"
              menuTestId={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.companyMenu}
              onChange={companyId => {
                setSelectedCompanyId(companyId);
                setSelectedParentFolderId(null);
              }}
              options={companyOptions}
              placeholder="Select Company"
              searchPlaceholder="Find a Company"
              searchTestId={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.companySearch}
              selectTestId={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.companySelect}
              selectedId={selectedCompanyId}
              testIdForOption={id => FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.companyOption(id ?? 0)}
            />
          ) : null}
          <FirmDocumentDialogSearchableSelect
            clearSearchAriaLabel="Clear parent folder search"
            disabled={isCreating || !activeMdDocumentsId}
            emptyLabel="No folders found"
            itemLabelPlural="folders"
            itemLabelSingular="folder"
            label="Parent Folder"
            menuTestId={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.parentFolderMenu}
            onChange={setSelectedParentFolderId}
            options={activeParentFolderOptions}
            placeholder="Select Parent Folder"
            renderOptionLabel={option => <FirmDocumentFolderPathLabel label={option.label} />}
            searchPlaceholder="Find a Folder"
            searchTestId={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.parentFolderSearch}
            selectTestId={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.parentFolderSelect}
            selectedId={selectedParentFolderId}
            testIdForOption={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.parentFolderOption}
          />
          <FirmDocumentFolderCreateDialogField>
            <FirmDocumentFolderCreateDialogLabel htmlFor={FOLDER_NAME_INPUT_ID}>
              Folder Name
            </FirmDocumentFolderCreateDialogLabel>
            <FirmDocumentFolderCreateDialogInput
              data-testid={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.input}
              disabled={isCreating}
              error={Boolean(helperText)}
              helperText={helperText ?? ' '}
              id={FOLDER_NAME_INPUT_ID}
              inputRef={inputRef}
              onChange={event => setFolderName(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={selectedParentOption?.id ? 'Enter subfolder name' : 'Enter folder name'}
              value={folderName}
            />
          </FirmDocumentFolderCreateDialogField>
        </FirmDocumentFolderCreateDialogFieldGroup>
      </DialogContent>
      <DialogActionFooter
        actionButtonTestId={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.confirmButton}
        actionLabel="Create Folder"
        cancelButtonTestId={FIRM_DOCUMENT_FOLDER_CREATE_DIALOG_TEST_IDS.cancelButton}
        isActionDisabled={!canCreateFolder}
        isActionLoading={isCreating}
        isCancelDisabled={isCreating}
        onAction={handleConfirm}
        onCancel={onClose}
      />
    </Dialog>
  );
};

export default FirmDocumentFolderCreateDialog;
